// in src/users.js
import { makeStyles, CircularProgress } from '@material-ui/core';
import React, { Fragment, useEffect, useState, useMemo } from 'react';
import {
  Datagrid,
  List,
  Responsive,
  TextInput,
  SelectField,
  SelectInput,
  SimpleList,
  TextField,
  TopToolbar,
  Pagination,
  useListContext,
  useQuery
} from 'react-admin';

import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import LinkField from '../../core/components/LinkField';
import Empty from '../../core/components/list/Empty';
import Filter from '../../core/components/list/StyledFilter';
import ListTitleBar from './importusers/ExcelContactImportTitleBarWithDropbox';
import StatusSelectField from '../../core/components/StatusSelectField';
import { UserIcon } from '../../core/themes/icons/UserIcon';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import { userStatus, userTypeSA } from './UserLookups';
import AutocompleteInput from '../../core/components/forked/input/AutocompleteInput';

const useStyles = makeStyles({
  dataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '24px'
      }
    }
  },
  statusColumnStyle: {
    width: '74px'
  },
  root: {
    marginRight: 'auto'
  },
  progress: {
    position: 'absolute',
    top: '55%',
    left: '55%'
  }
});

const Actions = ({
  displayedFilters,
  filters,
  filterValues,
  resource,
  showFilter
}) => {
  const classes = useStyles();
  return (
    <TopToolbar classes={{ root: classes.root }}>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button'
        })}
    </TopToolbar>
  );
};

const UserFilters = ({ filterValues, setAppliedFilters, ...props }) => {
  const isMupTenant = sessionStorage.getItem('isMupTenant') === 'true';
  const isMupChild = sessionStorage.getItem('isMupChild') === 'true';
  const { data } = useQuery({
    type: 'getList',
    resource: 'fundfamilies',
    payload: {
      filter: {
        orderBy: 'name',
        orderDirection: 'ASC'
      }
    }
  });

  useEffect(() => {
    if (filterValues) {
      setAppliedFilters(filterValues);
    }
  }, [filterValues, setAppliedFilters]);

  return (
    <Filter variant="standard" style={{ alignSelf: 'flex-end' }} {...props}>
      <TextInput
        label="Search by Name"
        source="name"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      {data && data.length > 0 && (
        <AutocompleteInput
          source="fundFamilyId"
          label="Fund Families"
          optionText="name"
          autoComplete="off"
          showArrowDown={true}
          clientSideFilterOnly={true}
          choices={data}
          alwaysOn
          emptyText="All Fund Families"
          placeholder="All Fund Families"
          id="autocomplete-input-fund-families"
          menuId="autocomplete-input-menu-fund-families"
          getItemId={itemIndex =>
            `autocomplete-input-menu-item-fund-families-${itemIndex}`
          }
        />
      )}
      {isMupTenant && (
        <TextInput
          label="User Login ID"
          source={isMupTenant && !isMupChild ? 'id' : 'mupUserId'}
          allowEmpty
          alwaysOn
          parse={predictiveSearchParser}
          format={predictiveSearchFormatter}
          options={{ autoComplete: 'off' }}
        />
      )}
      {!isMupTenant && (
        <TextInput
          label="User ID"
          source="id"
          allowEmpty
          alwaysOn
          parse={predictiveSearchParser}
          format={predictiveSearchFormatter}
          options={{ autoComplete: 'off' }}
        />
      )}
      <TextInput
        label="Email"
        source="email"
        allowEmpty
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      {isMupChild && isMupTenant && (
        <TextInput
          label="User ID"
          source="id"
          allowEmpty
          alwaysOn={false}
          parse={predictiveSearchParser}
          format={predictiveSearchFormatter}
          options={{ autoComplete: 'off' }}
        />
      )}
      <SelectInput
        label="Status"
        source="status"
        choices={userStatus}
        allowEmpty
      />
    </Filter>
  );
};

const UsersTitle = props => {
  const { loading } = useListContext(props);

  useEffect(() => props.setshowGridLoader(loading));

  return (
    <ListTitleBar
      type="Contacts"
      title="Create New Contact"
      createUrl="#/users/create"
      icon={<UserIcon />}
      hasCreatePermission={props.hasCreatePermission}
      hasImportUserExcelPermission={props.hasImportUserExcelPermission}
      hasExportUsersExcelPermission={props.hasExportUsersExcelPermission}
      filters={props.filters}
      selectedIds={props.selectedIds}
      {...props}
      id="new-contact"
    />
  );
};

const UserList = props => {
  const classes = useStyles();
  const {
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    hasReadPermission,
    userProfile,
    ...rest
  } = props;
  const [showGridLoader, setshowGridLoader] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const isMupTenant = sessionStorage.getItem('isMupTenant') === 'true';
  const isMupChild = sessionStorage.getItem('isMupChild') === 'true';
  const { hasImportUserExcelPermission } = useMemo(() => {
    return {
      hasImportUserExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_IMPORT_USER_EXCEL')
        : false
    };
  }, [props.permissions]);

  const { hasExportUsersExcelPermission } = useMemo(() => {
    return {
      hasExportUsersExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_EXPORT_CONTACTS')
        : false
    };
  }, [props.permissions]);

  const [appliedFilters, setAppliedFilters] = useState([]);
  return (
    <Fragment>
      <List
        {...rest}
        title={
          <UsersTitle
            hasCreatePermission={hasCreatePermission}
            hasExportUsersExcelPermission={hasExportUsersExcelPermission}
            hasImportUserExcelPermission={hasImportUserExcelPermission}
            setshowGridLoader={setshowGridLoader}
            filters={appliedFilters}
            props={props}
            selectedIds={selectedIds}
          />
        }
        sort={{ field: 'fullName', order: 'ASC' }}
        exporter={false}
        filters={<UserFilters setAppliedFilters={setAppliedFilters} />}
        perPage={20}
        pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
        filter={{ orphanVisibility: 'ALL_VISIBLE' }}
        bulkActionButtons={hasDeletePermission && <BulkActionDeleteButton />}
        actions={<Actions />}
        empty={<Empty />}
        syncWithLocation
      >
        <Responsive
          small={
            <SimpleList
              primaryText={record =>
                `${record.firstName ? record.firstName : ''} ${
                  record.lastName ? record.lastName : ''
                }`
              }
              secondaryText={record => record.userId}
              tertiaryText={() => ''}
            />
          }
          medium={
            <DatagridWithListContext
              hasDeletePermission={hasDeletePermission}
              classes={classes}
              isMupChild={isMupChild}
              isMupTenant={isMupTenant}
              setSelectedIds={setSelectedIds}
              {...rest}
            />
          }
        />
      </List>
      {showGridLoader && (
        <div className={classes.progress}>
          <CircularProgress />
        </div>
      )}
    </Fragment>
  );
};

const DatagridWithListContext = props => {
  const {
    hasDeletePermission,
    classes,
    isMupChild,
    isMupTenant,
    setSelectedIds,
    ...dataGridProps
  } = props;
  const { selectedIds } = useListContext(props);

  useEffect(() => {
    setSelectedIds(selectedIds);
  }, [selectedIds, setSelectedIds]);

  return (
    <>
      <Datagrid
        {...dataGridProps}
        classes={{ thead: hasDeletePermission && classes.dataGridHead }}
      >
        <LinkField
          label="Contact Name"
          sortBy="fullName"
          render={record =>
            `${record.firstName ? record.firstName : ''} ${
              record.lastName ? record.lastName : ''
            }`
          }
        />
        {isMupTenant && (
          <TextField
            label="User Login ID"
            source={isMupTenant && !isMupChild ? 'userId' : 'mupUserId'}
            sortBy={isMupTenant && !isMupChild ? 'id' : 'mupUserId'}
            id="user-login-id"
          />
        )}
        {(isMupChild || !isMupTenant) && (
          <TextField label="User ID" source="userId" sortBy="id" id="user-id" />
        )}
        <SelectField
          label="User Type"
          source="userType"
          choices={userTypeSA}
          id="user-type"
        />
        <TextField label="Email Address" source="email" id="email-address" />
        <TextField source="company" id="company" />
        <StatusSelectField
          label="Status"
          source="status"
          choices={userStatus}
          headerClassName={classes.statusColumnStyle}
          id="status"
        />
      </Datagrid>
    </>
  );
};

export default UserList;
